package dev.lingvo.site.components.automation

import com.varabyte.kobweb.navigation.Router
import dev.lingvo.shared.QaIssueDto
import dev.lingvo.shared.QaSeverity
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.ToastState
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.CancellationException

/**
 * Where "fix the first QA error" lands: the failing string, in the language it fails in.
 *
 * `?key=` is the editor's own deep link (`translate/[locale].kt` reads it as `initialKeyId`), so
 * this opens the workbench already on the string rather than on a queue the reader then has to
 * search. The locale comes from the issue rather than from the caller, because a project-wide ask
 * ("fix the first QA error in Storefront") does not know which language failed until the server
 * says.
 *
 * `&search=` is not redundant beside `?key=`. `key` selects the string; `search` narrows the queue
 * around it, so the reader lands on a short list holding the failing string rather than on one
 * highlighted row inside four hundred. Every hand-written call site that predates this helper
 * (`Qa.kt`, `strings/[keyId].kt`) appends both for that reason.
 */
fun qaIssueHref(projectSlug: String, issue: QaIssueDto): String =
    "/app/projects/$projectSlug/translate/${encodeURIComponent(issue.locale)}" +
        "?key=${encodeURIComponent(issue.keyId)}&search=${encodeURIComponent(issue.keyName)}"

private external fun encodeURIComponent(value: String): String

/**
 * Opens the first blocking QA error, or says honestly that there is not one.
 *
 * **One error, asked for as one error.** The readiness row, the QA page header, the dashboard tile
 * and the editor all want the same thing, and each of them used to be a different sentence pointing
 * at a list. `limit = 1` with `severity = ERROR` is the whole request: the server filters severity
 * before it applies the cap (`KeyRoutes.kt`), so this is genuinely the first *error* and not the
 * first issue that happens to be one.
 *
 * The outcome is reported through [toasts] when one is given, because two of the three outcomes are
 * invisible otherwise: a project with no errors left navigates nowhere, and a failed request looks
 * exactly the same as a press that did not register.
 *
 * @param locale narrow to one language, or null for the project.
 * @return the issue that was opened, or null when there was none and nothing was navigated to.
 */
suspend fun openFirstQaError(
    router: Router,
    projectSlug: String,
    locale: String? = null,
    toasts: ToastState? = null,
): QaIssueDto? {
    val issue = try {
        ApiClient.qa(
            projectSlug = projectSlug,
            locale = locale,
            severity = QaSeverity.ERROR,
            limit = 1,
        ).firstOrNull()
    } catch (cancelled: CancellationException) {
        throw cancelled
    } catch (failure: Throwable) {
        toasts?.show(
            (failure as? ApiException)?.message?.takeIf(String::isNotBlank)
                ?: tr(
                    "automation.first_qa_error.couldnt_read_qa_for_this_project",
                    "Couldn't read QA for this project. Try again.",
                ),
            LToastVariant.DANGER,
        )
        return null
    }
    if (issue == null) {
        toasts?.show(
            tr("automation.first_qa_error.no_qa_errors_left_here", "No QA errors left here."),
            LToastVariant.SUCCESS,
        )
        return null
    }
    router.tryRoutingTo(qaIssueHref(projectSlug, issue))
    return issue
}
